import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import useFetch from '../../hooks/useFetch';
import IntroducerIncomeCalculator from "./IntroducerIncomeCalculator";
import { logout } from "../../redux/actions/authActions";

const SheEarnsIntroduce = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const [fetchData, { loading }] = useFetch();

  const isLoggedIn = authState?.isLoggedIn || false;
  const introducerId = authState.user._id;
  const introducerName = authState?.user?.name || "User";
  const introducerThink5WhysCode = authState?.user?.think5whyscode || ""; 
  const [createSuccess, setCreateSuccess] = useState(false);
  const [createSuccessMsg, setCreateSuccessMsg] = useState('');
  const [introducedList, setIntroducedList] = useState([]);
  const [listError, setListError] = useState("");
  const [listFetched, setListFetched] = useState(false);
  const [showList, setShowList] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/sheearns");
  };

  const [code, setCode] = useState("");
  const [memberDetails, setMemberDetails] = useState(null);
  const [validationStatus, setValidationStatus] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [error, setError] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [referralName, setReferralName] = useState("");
  const [referralEmail, setReferralEmail] = useState("");

  const saveIntroduced = useCallback(() => {
    const payload = { 
      introducerId, 
      introducerName, 
      introducerThink5WhysCode, 
      introducedId: code, 
      introducedName: referralName, 
      introducedThink5WhysCode: code,
      introducerType: 'SAKSHAM Shakti',
      isActive: true,    
      isDeleted: false,  
      createdBy: introducerId, 
      dateCreated: new Date(),
    };

    const config = {
      url: "/introducer",
      method: "post",
      data: payload,
      headers: { Authorization: authState.token },
    };

    fetchData(config)
      .then((response) => {
        if (response && response.status) {
          setCreateSuccess(true);
          setCreateSuccessMsg("Introducer Created Successfully!");
        } else {
          setCreateSuccess(false);
          setCreateSuccessMsg("Record NOT created. Please retry.");
        }
      })
      .catch(() => {
        setCreateSuccess(false);
        setCreateSuccessMsg("Error saving record. Try again.");
      });
  }, [
    introducerId,
    introducerName,
    introducerThink5WhysCode,
    code,
    referralName,
    fetchData,
    authState.token,
  ]);

  const fetchIntroducedList = useCallback(() => {
  const config = {
    url: "/introducer/byid",
    method: "post",
    data: { introducerId },
    headers: { Authorization: authState.token }
  };

  fetchData(config)
    .then((response) => {
      if (response && response.status && Array.isArray(response.introducer)) {
        setIntroducedList(response.introducer); 
        setShowList(true);
        setListFetched(true);
        setListError("");
      } else {
        setIntroducedList([]);
        setShowList(true);
        setListFetched(true);
        setListError(response?.message || "No introduced members found.");
      }
    })
    .catch(() => {
      setIntroducedList([]);
      setShowList(true);
      setListFetched(true);
      setListError("Failed to fetch introduced members. Please try again.");
    });
}, [introducerId, authState.token, fetchData]);



  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 14);
    setCode(value);
    if (error) setError("");
  };

  const fetchNewMemberCode = useCallback((newMemberCode) => {
    const config = {
      url: "/auth/verifyreferralcode",
      method: "post",
      headers: { Authorization: authState.token },
      data: { think5whyscode: newMemberCode }
    };

    fetchData(config)
      .then(response => {
        if (response && response.status) {
          setReferralName(response.t5wcode.name);
          setReferralEmail(response.t5wcode.email);
          // setReferralPlace(response.t5wcode.place || "Not specified"); 
          setValidationStatus(true);
          setStatusMessage(`Code ${newMemberCode} verified! 😊`);
          setMemberDetails({
            name: response.t5wcode.name,
            email: response.t5wcode.email,
            place: response.t5wcode.place || "Not specified"
          });
        } else {
          setValidationStatus(false);
          setStatusMessage(`Code ${newMemberCode} not found! 😔`);
          setError("Member not found or invalid code.");
        }
      })
      .catch(err => {
        console.error("Verification error:", err);
        setValidationStatus(false);
        setStatusMessage(`Code ${newMemberCode} not found! 😔`);
        setError("Verification failed. Please try again.");
      })
      .finally(() => {
        setIsValidating(false);
      });
  }, [authState.token]);

  const newMemberCodeValidationClicked = useCallback((newMemberCode) => {
    if (/^[A-Z0-9]{14}$/.test(newMemberCode)) {
      if (introducerThink5WhysCode === newMemberCode) {
        setError("You cannot introduce yourself. Please use a different valid code.");
        setIsValidating(false);
      } else {
        fetchNewMemberCode(newMemberCode);
      }
    } else {
      setError("Please enter a valid 14-character alphanumeric Think5Whys Code.");
      setIsValidating(false);
    }
  }, [introducerThink5WhysCode, fetchNewMemberCode]);

  const handleNewMemberCodeValidation = () => {
    setIsValidating(true);
  };

  const handleVerify = () => {
    if (!isLoggedIn) {
      setError("Please log in to verify a member.");
      return;
    }
    setError("");
    setStatusMessage("");
    handleNewMemberCodeValidation();
    newMemberCodeValidationClicked(code);
  };

  const resetForm = () => {
    setCode("");
    setMemberDetails(null);
    setValidationStatus(false);
    setStatusMessage("");
    setError("");
    setReferralName("");
    setReferralEmail("");
    setReferralPlace("");
  };

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/login"); // Redirect if not logged in
      return;
    }
  }, [isLoggedIn, navigate]);

  return (
    <div className="bg-black min-h-screen text-white">
      <div className="flex flex-col bg-[#1a0b1e] text-pink-100 font-sans">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-[#2a0f2f] border-b border-pink-800 px-4 py-2 flex items-center justify-between shadow-lg">
          <div className="flex flex-col">
            <div className="text-xl lg:text-2xl font-bold text-pink-400">Think5Whys</div>
            <div className="text-[10px] lg:text-lg text-yellow-200">
              Introduce New Member
            </div>
          </div>

          {/* Desktop user controls */}
          <div className="hidden md:flex items-center gap-3">
            {isLoggedIn ? (
              <>
                <div className="text-xs text-left text-pink-300">{introducerName}</div>
                <button
                  onClick={handleLogout}
                  className="p-2 mt-2 rounded-md bg-rose-600 hover:bg-rose-700 transition"
                  title="Logout"
                >
                  ⏻
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate("/login")}
                  className="px-3 py-1 rounded-full border border-pink-700 text-sm text-pink-200 hover:bg-pink-900"
                >
                  Login
                </button>
                <button
                  onClick={() => navigate("/signup")}
                  className="px-3 py-1 rounded-full bg-pink-500 text-white text-sm hover:bg-pink-400"
                >
                  Register
                </button>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setIsMobileMenuOpen((s) => !s)}
            className="md:hidden w-6 h-6 flex items-center justify-center text-xl text-pink-300 bg-pink-500"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? "×" : "☰"}
          </button>
        </header>

        {/* Mobile menu content */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-[#2a0f2f] border-b border-pink-800 px-4 py-2 animate-fadeIn">
            {isLoggedIn ? (
              <div className="flex flex-row items-center justify-center gap-3">
                <span className="text-pink-300 font-medium whitespace-nowrap h-7">
                  Welcome, {introducerName}
                </span>
                <button
                  onClick={handleLogout}
                  className="rounded-md bg-rose-600 hover:bg-rose-700 w-7 h-7 flex items-center justify-center text-white"
                  title="Logout"
                >
                  ⏻
                </button>
              </div>
            ) : (
              <div className="flex flex-row items-center justify-center gap-6">
                <Link to="/login" className="text-pink-300 hover:text-yellow-300">
                  Login
                </Link>
                <span className="text-pink-500">|</span>
                <Link to="/signup" className="text-pink-300 hover:text-yellow-300">
                  Register
                </Link>
              </div>
            )}
          </div>
        )}
      </div>

      <section className="bg-black py-8 px-4">
        <div className="mx-auto">
          <div className="mb-2 text-center">
            <h2 className="text-xl lg:text-2xl font-extrabold bg-gradient-to-r from-pink-500 to-pink-600 bg-clip-text text-transparent mb-4">
              👋 Benefit of Introducing a New Member
            </h2>
            <div className="text-pink-200 text-sm lg:text-base mx-auto space-y-2">
              <ol className="list-decimal list-inside text-center">
                <li>You will get additional income of whatever they purchase from Think5Whys platform.</li>
                <li>You will get 0.5% of your immediate referral purchases, then will get 0.3% for their referral purchases and 0.2% for each up to Level 8 down the line.</li>
                <li>Terms & Conditions Apply</li>
              </ol>
            </div>
          </div>

          <div className="flex justify-center mb-4">
            <button
              onClick={() => setShowCalculator(true)}
              className="bg-yellow-600 text-white font-semibold py-2 px-6 w-60 rounded-lg shadow-md hover:shadow-lg"
            >
              💰 Estimate Income
            </button>
          </div>

          {showCalculator && (
            <IntroducerIncomeCalculator onClose={() => setShowCalculator(false)} />
          )}

          <div className="bg-gray-800 rounded-2xl p-6 shadow-md border border-pink-800 mt-4 mb-2">
            <p className="text-pink-300 mb-3 text-center">
              Use New Member Think5Whys Code to Enroll Under Network
            </p>
            <p className="text-pink-200 mb-3 text-center text-sm">
              Ask the new member for their code from Home → Profile → My Think5Whys Code
            </p>
          </div>

          {!memberDetails ? (
            <>
              <div className="bg-gray-800 rounded-2xl p-6 shadow-md border border-pink-800">
                <div className="flex text-wrap justify-center mb-3">
                  <p className="text-yellow-400">Input 14 Characters Alphanumeric New Member Code (e.g., TU243AM5850150):</p>
                </div>
                <div className="mb-4 flex text-wrap justify-center">
                  <p className="text-pink-200 text-sm">Ask the new member to provide their code from Home → Profile → My Think5Whys Code</p>
                </div>
                <div className="flex justify-center mt-6">
                  <button
                    className="bg-purple-700 text-white font-semibold w-64 py-2 px-4 rounded-lg shadow-md hover:bg-purple-800 transition disabled:opacity-50"
                    onClick={fetchIntroducedList}
                    disabled={listFetched}       
                  >
                    {listFetched ? "List Loaded ✔" : "My Introduced Members List"}
                  </button>
                </div>
                {showList && (
                  <div className="bg-gray-900 border border-purple-700 rounded-xl p-6 mt-6 mx-4 shadow-lg text-white">
                    
                    <h3 className="text-xl font-bold text-purple-400 text-center mb-4">
                      👥 My Introduced Members
                    </h3>

                    {listError && (
                      <p className="text-red-400 text-center">{listError}</p>
                    )}

                    {!listError && introducedList.length === 0 && (
                      <p className="text-yellow-400 text-center">No members introduced yet.</p>
                    )}

                    {introducedList.length > 0 && (
                      <div className="space-y-4">
                        {introducedList.map((item, index) => (
                          <div key={index} className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                            <p><span className="text-gray-400">Name:</span> {item.introducedName}</p>
                            <p><span className="text-gray-400">Think5Whys Code:</span> {item.introducedThink5WhysCode}</p>
                            <p>
                              <span className="text-gray-400">Date of Introduction:</span> 
                              {item.dateCreated ? new Date(item.dateCreated).toLocaleDateString() : "-"}
                            </p>
                            <p>
                              <span className="text-gray-400">Active:</span> 
                              {item.isActive ? "Yes" : "No"}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex justify-center mt-5">
                      <button
                        onClick={() => setShowList(false)}
                        className="bg-red-600 w-24 px-4 py-2 rounded-lg text-white hover:bg-red-700 transition"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}

                <div className="mt-2 mb-2 flex justify-center">
                  <input
                    type="text"
                    value={code}
                    onChange={handleCodeChange}
                    placeholder="Input New Member Code"
                    className="h-12 text-center p-2 border border-pink-700 rounded-lg bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 w-full"
                    maxLength={14}
                  />
                </div>
                <div className="mb-4 flex justify-center">
                  {error && <p className="text-red-400 text-sm text-center">{error}</p>}
                </div>
                <div className="mb-4 flex justify-center">
                  <p className={validationStatus ? "text-green-400 text-sm text-center" : "text-red-500 text-sm text-center"}>
                    {statusMessage}
                  </p>
                </div>
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={handleVerify}
                    disabled={code.length !== 14 || isValidating || !isLoggedIn}
                    className="bg-pink-600 text-white py-3 px-6 w-40 rounded-lg shadow-md hover:shadow-lg font-semibold"
                  >
                    {isValidating ? "Verifying..." : "Verify Member"}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-gray-800 rounded-2xl p-6 shadow-md border border-green-800">
              <h3 className="text-xl font-bold text-green-400 mb-4 text-center">✅ Member Verified!</h3>
              <div className="details-container w-full mx-auto text-white bg-slate-800 rounded-lg shadow-lg p-4 mb-4">
                {code && (
                  <div className="flex justify-center mb-4">
                    <p className="text-white text-lg">New Member Name: <strong className="text-yellow-300 text-lg">{referralName}</strong></p>
                  </div>
                )}
                <div className="space-y-3 text-center">
                  <div className="flex justify-center">
                    <span className="text-gray-400">Email ID:</span>
                    <span className="font-semibold text-white">{referralEmail}</span>
                  </div>
                  <div className="flex justify-center">
                    <span className="text-gray-400">Think5Whys Code:</span>
                    <span className="font-semibold text-white">{code}</span>
                  </div>
                </div>
              </div>
              {/* TODO: Add enrollment or next step button here, e.g., proceed to enroll with wallet check if needed */}
              <div className="flex justify-center mt-4 space-x-4">
                <button
                  className="bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md hover:shadow-lg hover:-translate-y-0.5 transition disabled:opacity-50"
                  disabled={!validationStatus || createSuccess}
                  onClick={saveIntroduced}
                >
                  {createSuccess ? "Introduced ✔" : "Proceed to Introduce"}
                </button>
              </div>
              {createSuccessMsg && (
                <p className="text-center mt-3 text-yellow-300 text-sm">{createSuccessMsg}</p>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2a0f2f] border-t border-pink-800 text-pink-300 py-4">
        <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-xs lg:text-sm">
            © 2025 Think5Whys Global DeepTech Solutions. All rights reserved.
          </div>
          <div className="flex flex-wrap gap-4 text-xs lg:text-sm">
            <Link to="/about-us" className="text-white hover:text-pink-400">About</Link>
            <Link to="/privacy-policy" className="text-white hover:text-pink-400">Privacy Policy</Link>
            <Link to="/terms-of-use" className="text-white hover:text-pink-400">Terms of Use</Link>
            <Link to="/contact-us" className="text-white hover:text-pink-400">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SheEarnsIntroduce;